import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Alert, Box, Button, Typography } from '@mui/material';
import { styled, useTheme } from "@mui/material/styles";
import { useNavigate } from 'react-router-dom';
import useMediaQuery from "@mui/material/useMediaQuery";
import { getFirebaseAuthModule  } from '../../Repository/Authentication/Firebase';
import { useAuthUser } from '../../Context/AuthUserContext';
import {
    clearPendingQrReturn,
    decodeCamiCodeEncoded,
    hasQrLinkParams,
    parseQrLinkText,
    resolveCamiCodeFromLink,
    verifyCamiCodeScan,
} from '../../Business/CamiCodeScanBusiness';

const ActionButton = styled(Button)(({ theme }) => ({
    color: "#FFFFFF",
    backgroundColor: theme.palette.secondary.main,
    borderRadius: 999,
    fontWeight: 600,
    textTransform: "none",
    width: "100%",
    boxShadow: "0 4px 14px rgba(176, 24, 64, 0.28)",
    "&:hover": {
        backgroundColor: theme.palette.secondary.dark,
    },
}));

const ScanCamiCode = () => {
    const theme = useTheme();
    let [result, setResult] = useState("");
    let [feedback, setFeedback] = useState({});
    let [uniqIdFromResult, setUniqIdFromResult] = useState("")
    const [background, setBackground] = useState({ backgroundColor: theme.palette.primary.main });
    const scannerRef = useRef(null);
    const scannerCancelRef = useRef(false);
    const hasScannedRef = useRef(false);
    const linkHandledRef = useRef(false);
    const scanSessionRef = useRef(0);
    const navigate = useNavigate();
    const { profile, authReady, isLoggedIn, profileLoading, refreshProfile } = useAuthUser();
    const smallDevices = useMediaQuery(theme.breakpoints.down("md"));
    const [password, setPassword] = useState('');
    const [camiCode, setCamiCode] = useState('');
    const [user,setUser]=useState("");
    const [showNoVideoError, setShowNoVideoError] = useState(false);
    const [noVideoErrorMessage, setNoVideoErrorMessage] = useState("");
    const [scanOptions, setScanOptions] = useState(null);
    const [latitude, setLatitude] = useState("");
    const [CameraError, setCameraError] = useState("");
    const [longitude, setLongitude] = useState("");
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [linkBootstrapLoading, setLinkBootstrapLoading] = useState(true);

    const showScannerUi =
        !scanOptions &&
        !showNoVideoError &&
        !verifyLoading &&
        !linkBootstrapLoading;

    const getOptionalAuthHeaders = () => {
        const token = localStorage.getItem('JWTTOKEN');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const releaseCameraTracks = () => {
        const reader = document.getElementById("reader");
        if (!reader) {
            return;
        }
        reader.querySelectorAll("video").forEach((video) => {
            const stream = video.srcObject;
            if (stream && typeof stream.getTracks === "function") {
                stream.getTracks().forEach((track) => {
                    try {
                        track.stop();
                    } catch (error) {
                        // ignore
                    }
                });
            }
            try {
                video.srcObject = null;
                video.pause();
            } catch (error) {
                // ignore
            }
        });
    };

    const safeStopScanner = async (scanner) => {
        const target = scanner || scannerRef.current;
        if (!target) {
            releaseCameraTracks();
            return;
        }

        // Always attempt stop — state can lag while start() is still opening the camera.
        try {
            await target.stop();
        } catch (error) {
            // Not running / already stopped is expected during cleanup races.
        }

        try {
            if (typeof target.clear === "function") {
                await target.clear();
            }
        } catch (error) {
            // clear can fail if the element was already torn down
        }

        releaseCameraTracks();

        if (scannerRef.current === target) {
            scannerRef.current = null;
        }
    };

    const resetToFreshScan = async () => {
        scanSessionRef.current += 1;
        scannerCancelRef.current = true;
        hasScannedRef.current = false;
        linkHandledRef.current = false;
        await safeStopScanner(scannerRef.current);

        setResult("");
        setFeedback({});
        setUniqIdFromResult("");
        setPassword("");
        setCamiCode("");
        setShowNoVideoError(false);
        setNoVideoErrorMessage("");
        setScanOptions(null);
        setCameraError("");
        setVerifyLoading(false);
        setLinkBootstrapLoading(false);
        clearPendingQrReturn();

        // Drop ?c=&p= (and any encoded token path) so bootstrap does not re-open the same QR.
        navigate("/scan-qr", { replace: true });
        // Allow the scanner effect to start a new session on the next tick.
        scannerCancelRef.current = false;
    };

    useEffect(() => {
        const auth = getFirebaseAuthModule();
        const unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
            user.getIdToken().then((newToken) => {
              localStorage.setItem("JWTTOKEN", newToken);
            setUser(user);
            });
          } else {
            setUser("");
          }
        }
        );
        return () => unsubscribe();
      }, []);

    useEffect(() => {
        async function bootstrap() {
            try {
                const resolved = await resolveCamiCodeFromLink(window.location, getOptionalAuthHeaders);
                if (resolved.camiCode && resolved.password) {
                    linkHandledRef.current = true;
                    hasScannedRef.current = true;
                    setCamiCode(resolved.camiCode);
                    setPassword(resolved.password);
                } else if (hasQrLinkParams(window.location)) {
                    // Direct link open (camera / external scanner) that could not
                    // be resolved — do not fall through to the in-app camera UI.
                    setNoVideoErrorMessage("Could not open this QR link.");
                    setShowNoVideoError(true);
                }
            } catch (error) {
                setFeedback({ status: 'excecption', message: error });
                if (hasQrLinkParams(window.location)) {
                    setNoVideoErrorMessage(
                        error?.message || "Could not open this QR link."
                    );
                    setShowNoVideoError(true);
                }
            } finally {
                setLinkBootstrapLoading(false);
            }
        }

        bootstrap();
    }, []);

    useEffect(() => {
        if (linkBootstrapLoading || verifyLoading || showNoVideoError || scanOptions || camiCode || password) {
            scannerCancelRef.current = true;
            safeStopScanner(scannerRef.current);
            return undefined;
        }

        let stopScanning;
        let cancelled = false;
        scannerCancelRef.current = false;

        const mountScanner = async () => {
            await safeStopScanner(scannerRef.current);
            if (cancelled || scannerCancelRef.current || !document.getElementById("reader")) {
                return;
            }
            stopScanning = startScanning();
        };

        const frameId = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                mountScanner();
            });
        });

        return () => {
            cancelled = true;
            scannerCancelRef.current = true;
            window.cancelAnimationFrame(frameId);
            if (typeof stopScanning === "function") {
                stopScanning();
            }
            safeStopScanner(scannerRef.current);
        };
    }, [linkBootstrapLoading, verifyLoading, showNoVideoError, scanOptions, camiCode, password]);

    // Always release the camera if this page unmounts (nav away / close).
    useEffect(() => {
        return () => {
            scannerCancelRef.current = true;
            safeStopScanner(scannerRef.current);
        };
    }, []);

    useEffect(() => {
        if (result) {
            const parsed = parseQrLinkText(result);
            if (parsed.camiCode && parsed.password) {
                hasScannedRef.current = true;
                setCamiCode(parsed.camiCode);
                setPassword(parsed.password);
                safeStopScanner(scannerRef.current);
                return;
            }
            setUniqIdFromResult(parsed.encoded || "");
        }
    }, [result]);

    useEffect(() => {
        async function decodeScannedToken() {
            if (!uniqIdFromResult || (camiCode && password)) {
                return;
            }

            try {
                const decoded = await decodeCamiCodeEncoded(uniqIdFromResult, getOptionalAuthHeaders);
                if (decoded.camiCode && decoded.password) {
                    setCamiCode(decoded.camiCode);
                    setPassword(decoded.password);
                }
            } catch (error) {
                setFeedback({ status: 'excecption', message: error });
            }
        }

        decodeScannedToken();
    }, [uniqIdFromResult, camiCode, password]);

    function startScanning() {
        if (!document.getElementById("reader") || scannerCancelRef.current) {
            return undefined;
        }
        setFeedback({});
        hasScannedRef.current = false;
        try{
            const html5QrCode = new Html5Qrcode("reader", {
                formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
            });
            scannerRef.current = html5QrCode;
            const qrCodeSuccessCallback = async (decodedText) => {
                if (hasScannedRef.current || scannerCancelRef.current) {
                    return;
                }
                hasScannedRef.current = true;
                setResult(decodedText);
                await safeStopScanner(html5QrCode);
            };
            const config = { fps: 10, qrbox: { width: 250, height: 250 } };
            if (navigator.geolocation) {
                const handlePosition = (position) => {
                  let latitude = position.coords.latitude;
                  setLatitude(latitude);
                  let longitude = position.coords.longitude;
                  setLongitude(longitude);
                }
                navigator.geolocation.getCurrentPosition(handlePosition);
              }
            html5QrCode.start(
                { facingMode: "environment" },
                config,
                qrCodeSuccessCallback,
            ).then(() => {
                // start() can finish after the user left — shut the camera down.
                if (scannerCancelRef.current || scannerRef.current !== html5QrCode) {
                    safeStopScanner(html5QrCode);
                }
            }).catch((error) => {
                if (scannerCancelRef.current) {
                    return;
                }
                if (error?.name === 'NotAllowedError') {
                    setCameraError(
                        "Your browser settings does not allow Bezohminds to use camera. Please update the settings."
                    );
                } else {
                    console.log(error);
                }
            });
            return () => {
                scannerCancelRef.current = true;
                safeStopScanner(html5QrCode);
            };
        }catch(error){
            console.log(error);
        }
    }

    useEffect(() => {
        if (!authReady) {
            return;
        }
        if (isLoggedIn && profileLoading && !profile) {
            return;
        }
        getPaymentStatus();
    }, [password, camiCode, user, profile, authReady, isLoggedIn, profileLoading]);

    function findlocation() {
        const _latlong = {
          camicode: camiCode,
          latitude: latitude,
          longitude: longitude,
        };
      }

    async function getPaymentStatus() {
        if (!password || !camiCode || !authReady) {
            return;
        }
        if (isLoggedIn && profileLoading && !profile) {
            return;
        }

        const sessionId = scanSessionRef.current;
        setVerifyLoading(true);
        try {
            const auth = getFirebaseAuthModule();
            let activeProfile = profile;
            if (isLoggedIn && (!activeProfile || activeProfile.id == null)) {
                try {
                    activeProfile = (await refreshProfile()) || activeProfile;
                } catch {
                    /* keep existing profile */
                }
            }
            const authUser =
                auth.currentUser ||
                user ||
                (isLoggedIn && activeProfile?.email ? { email: activeProfile.email } : null);

            const outcome = await verifyCamiCodeScan({
                camiCode,
                password,
                getAuthHeaders: getOptionalAuthHeaders,
                authUser,
                profile: activeProfile,
                redirPath: '/',
                showOwnerOptions: true,
            });

            if (sessionId !== scanSessionRef.current) {
                return;
            }

            if (outcome.action === 'owner-options') {
                clearPendingQrReturn();
                setScanOptions(outcome);
                return;
            }
            if (outcome.action === 'watch' || outcome.action === 'upload') {
                clearPendingQrReturn();
                navigate(outcome.url);
                return;
            }
            if (outcome.action === 'login') {
                // Auth can still be settling; retry verify once before showing a permission error.
                if (isLoggedIn || auth.currentUser) {
                    const retryOutcome = await verifyCamiCodeScan({
                        camiCode,
                        password,
                        getAuthHeaders: getOptionalAuthHeaders,
                        authUser:
                            auth.currentUser ||
                            user ||
                            (activeProfile?.email ? { email: activeProfile.email } : null),
                        profile: activeProfile,
                        redirPath: '/',
                        showOwnerOptions: true,
                    });

                    if (sessionId !== scanSessionRef.current) {
                        return;
                    }

                    if (retryOutcome.action === 'owner-options') {
                        clearPendingQrReturn();
                        setScanOptions(retryOutcome);
                        return;
                    }
                    if (retryOutcome.action === 'upload' && retryOutcome.url) {
                        clearPendingQrReturn();
                        navigate(retryOutcome.url);
                        return;
                    }
                    if (retryOutcome.action === 'watch' && retryOutcome.url) {
                        clearPendingQrReturn();
                        navigate(retryOutcome.url);
                        return;
                    }

                    setNoVideoErrorMessage(
                        retryOutcome.message ||
                            "There is no video on this QR code and you do not have permission to upload to it."
                    );
                    setShowNoVideoError(true);
                    return;
                }
                navigate('/login', {
                    state: outcome.loginState,
                    replace: true,
                });
                return;
            }
            if (outcome.action === 'error') {
                setNoVideoErrorMessage(outcome.message || "You cannot upload to this QR code.");
                setShowNoVideoError(true);
                return;
            }
        } catch (error) {
            if (sessionId !== scanSessionRef.current) {
                return;
            }
            setFeedback({ status: 'excecption', message: error });
        } finally {
            if (sessionId === scanSessionRef.current) {
                setVerifyLoading(false);
            }
        }
    }

    useEffect(() => {
        findlocation();
    }, [password, camiCode]);

    function onCloseAlertBox() {
        setCameraError("");
        resetToFreshScan();
    }

    function renderErrorFeedBack() {
        if (CameraError) {
          return (
            <Box sx={{ margin: "auto", width: "60%", paddingTop: "60px" }}>
              <Alert severity={"error"} onClose={onCloseAlertBox}>
                {CameraError}
              </Alert>
            </Box>
          );
        }
      }

    function renderAlertFeedback() {
        if (feedback['status']) {
            if (feedback['status'] === 'excecption') {
                const feedbackMessage =
                    feedback?.message?.message ||
                    String(feedback?.message || "Something went wrong while scanning the QR code.");
                return (
                    <Alert severity={'error'} onClose={onCloseAlertBox} >
                        {feedbackMessage}
                    </Alert>
                )
            }
        }

    }

    function renderScanOptions() {
        if (!scanOptions) {
            return null;
        }

        const { watchUrl, recordUrl, uploadUrl, title, videoCount, hasPlayableMedia } = scanOptions;

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "50vh",
                    px: 2,
                    py: 4,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.5, textAlign: "center" }}
                >
                    QR Code scanned
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, mb: 0.5, textAlign: "center" }}>
                    {title}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary, mb: 2, fontSize: "0.9rem", textAlign: "center" }}>
                    {hasPlayableMedia
                        ? (videoCount > 1
                            ? `${videoCount} videos available`
                            : "This QR code has a video ready to watch.")
                        : "No video yet — record or upload one."}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%", maxWidth: 320 }}>
                    <ActionButton variant="contained" onClick={() => navigate(recordUrl)}>
                        Record Video
                    </ActionButton>
                    <ActionButton
                        variant="contained"
                        onClick={() =>
                            navigate(uploadUrl, {
                                state: {
                                    skipUploadChooser: true,
                                    directUploadVideo: true,
                                },
                            })
                        }
                    >
                        Upload Video
                    </ActionButton>
                    {hasPlayableMedia && watchUrl ? (
                        <ActionButton
                            variant="contained"
                            onClick={() => {
                                const separator = watchUrl.includes("?") ? "&" : "?";
                                navigate(`${watchUrl}${separator}play=1`);
                            }}
                        >
                            Watch Video
                        </ActionButton>
                    ) : null}
                    <Button
                        variant="text"
                        onClick={resetToFreshScan}
                        sx={{ color: theme.palette.primary.main, fontWeight: 600, textTransform: "none" }}
                    >
                        Scan another QR code
                    </Button>
                </Box>
            </Box>
        );
    }

    function renderScannerSurface() {
        // Keep #reader in the DOM (hidden when not scanning) so stop()/clear()
        // can release MediaStream tracks before React tears the node down.
        return (
            <div
                id="reader"
                style={{
                    width: "100%",
                    maxWidth: smallDevices ? "100%" : "500px",
                    maxHeight: smallDevices ? "70vh" : "500px",
                    margin: "0 auto",
                    display: showScannerUi ? "block" : "none",
                }}
            />
        );
    }

   if (scanOptions) {
     return (
        <Box sx={{ paddingTop: smallDevices ? '60px' : '20px' }}>
            {renderScanOptions()}
            {renderScannerSurface()}
        </Box>
     );
   }

   if(!showNoVideoError){
     return ( <>
        <Box sx={{ paddingTop: smallDevices ? ('60px') : ('20px') }} >
            <Box sx={{ width: '100%', }}>
                <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: background.backgroundColor }} gutterBottom component='div' >
                        Scan QR Code
                    </Typography>
                    {(verifyLoading || linkBootstrapLoading) ? (
                        <Typography sx={{ color: theme.palette.text.secondary, mt: 1 }}>
                            {linkBootstrapLoading ? 'Opening QR link...' : 'Verifying QR code...'}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
        </Box>
        {renderAlertFeedback()}
        {renderErrorFeedBack()}
        {renderScannerSurface()}
    </>
    );
   }
   else{
    return (
        <Box sx={{ px: 2, py: smallDevices ? 8 : 4 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "40vh" }}>
                <Alert severity="error" onClose={resetToFreshScan}>
                    {noVideoErrorMessage}
                </Alert>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mt: 2 }}>
                <Button
                    variant="text"
                    onClick={resetToFreshScan}
                    sx={{ color: theme.palette.primary.main, fontWeight: 600, textTransform: "none" }}
                >
                    Scan another QR code
                </Button>
            </Box>
            {renderScannerSurface()}
        </Box>
    );
   }
}

export default ScanCamiCode;
